/**
 * Phase 45 Plan 45-05 — ManagerKpiCard.
 *
 * Local extension of dashboard/KpiCard that adds:
 *   - optional `delta` (signed text + tone class) to surface period-over-
 *     period change next to the value
 *   - optional `accent` ({ pct, color }) bottom progress bar — used by the
 *     Hours logged KPI to show how much of the budget is burned
 *
 * Renders `delta === null` as "—" per UI-SPEC §"show '—' when unavailable".
 *
 * A11y: card body wrapped in `role="group"` with `aria-labelledby` pointing
 * at the label span so screen readers announce the KPI by name (UI-SPEC).
 */
import { useId, type ReactNode } from 'react';

export type DeltaTone = 'good' | 'bad' | 'neutral';

export interface ManagerKpiCardProps {
  /**
   * MKS-332: a ReactNode, not a string, so a money tile can label itself with
   * `<MoneyMetricLabel>` and carry the same name AND definition tooltip its
   * column in the table below already carries. The `aria-labelledby` wrapper
   * is unchanged — it points at this node either way.
   */
  label: ReactNode;
  value: string | number;
  sub?: string;
  delta?: string | null;
  deltaTone?: DeltaTone;
  accent?: { pct: number; color: string } | null;
}

const TONE_CLASS: Record<DeltaTone, string> = {
  good: 'text-emerald-600',
  bad: 'text-red-600',
  neutral: 'text-ms-text-muted',
};

export function ManagerKpiCard({
  label,
  value,
  sub,
  delta,
  deltaTone = 'neutral',
  accent = null,
}: ManagerKpiCardProps) {
  const labelId = useId();
  return (
    <div
      className="bg-ms-surface border border-ms-border-subtle rounded-lg px-4 py-3"
      role="group"
      aria-labelledby={labelId}
    >
      <div
        id={labelId}
        className="text-[10px] uppercase tracking-wider text-ms-text-muted font-medium mb-1.5"
      >
        {label}
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-[22px] font-semibold tracking-tight text-ms-text-strong tabular-nums">
          {value}
        </span>
        <span className={`text-[11.5px] font-medium tabular-nums ${TONE_CLASS[deltaTone]}`}>
          {delta ?? '—'}
        </span>
      </div>
      {sub && <div className="text-[11px] text-ms-text-muted mt-0.5">{sub}</div>}
      {accent && (
        <div className="mt-2 h-1 rounded-full overflow-hidden bg-ms-surface-2">
          <div
            className="h-full rounded-full"
            style={{
              width: `${Math.min(100, Math.max(0, accent.pct))}%`,
              background: accent.color,
            }}
          />
        </div>
      )}
    </div>
  );
}
